import { Button, InlineFormInput } from '@platform/components';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'wouter';
import eyBackground from '../background-images/EYSIgnUp.png';
import f5Background from '../background-images/F5SignUp.png';
import reckittBackground from '../background-images/ReckittSignUp.png';
import sapBackground from '../background-images/SAPSignUp.png';
import { ForgotPasswordLink } from '../components/ForgotPasswordLink';
import { LogInBackground } from '../components/LogInBackground';
import { EmailOrPasswordIncorrectBox } from '../sign-in/EmailOrPasswordIncorrectBox';

type FormValues = {
  email: string;
  password: string;
};

/**
 * This login page is only shown after a user from a partner verifies their email.
 * Subsequent logins will go through the normal sign in page.
 */
export const OneTimeLoginPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' });

  const [invalidCredentials, setInvalidCredentials] = useState(false);

  const params = useParams();

  const isEY = params.partner?.toLowerCase() === 'ey';
  const isReckitt = params.partner?.toLowerCase() === 'reckitt';
  const isSap = params.partner?.toLowerCase() === 'sap';
  const isF5 = params.partner?.toLowerCase() === 'f5';

  //We use fetch here instead of the mutation + onSuccess/onError because
  //the cookie needs to be set on login and the mutation onSuccess
  //is in a race condition with the cookie being set
  const login: SubmitHandler<FormValues> = async (data) => {
    setInvalidCredentials(false);
    const loginTarget = import.meta.env.VITE_API_HOST + '/login';
    const response = await fetch(loginTarget, {
      method: 'POST',
      body: JSON.stringify({
        password: data.password,
        email: data.email,
      }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    const responseData = await response.json();

    if (response.status === 200) {
      window.location.href = '/platform/dashboard';
    } else if (response.status === 401) {
      setInvalidCredentials(true);
    } else if (response.status === 303) {
      if (responseData.next) {
        window.location.href = responseData.next;
      }
    }
  };

  let loginBackground;
  if (isReckitt) {
    loginBackground = reckittBackground;
  }
  if (isSap) {
    loginBackground = sapBackground;
  }
  if (isF5) {
    loginBackground = f5Background;
  }
  if (isEY) {
    loginBackground = eyBackground;
  }

  return (
    <LogInBackground background={loginBackground}>
      <div className='flex flex-col gap-8 w-[400px]'>
        <div className='flex flex-col gap-3'>
          <h3 className='text-display-sm font-gray-700 font-medium'>
            {getHeaderText(isReckitt, isSap, isF5, isEY)}
          </h3>
          <div>
            Use your work email and password you created to continue completing your application.
          </div>
        </div>

        <form className='flex flex-col gap-8' name='login' onSubmit={handleSubmit(login)}>
          <InlineFormInput
            label='Work email'
            errors={errors.email}
            placeholder='Enter your work email'
            register={() => {
              return register('email', {
                required: { value: true, message: 'Please enter your email address.' },
                pattern: { value: /^\S+@\S+$/i, message: 'Please enter a valid email address.' },
              });
            }}
          />
          <InlineFormInput
            label='Password'
            inputType='password'
            errors={errors.password}
            placeholder='Enter the password you created'
            register={() => {
              return register('password', {
                required: { value: true, message: 'Please enter your password.' },
              });
            }}
          />
          {invalidCredentials && <EmailOrPasswordIncorrectBox />}
          <Button type='submit' variant='primary' size='md'>
            Continue
          </Button>
        </form>
        <ForgotPasswordLink />
      </div>
    </LogInBackground>
  );
};

const getHeaderText = (isReckitt: boolean, isSap: boolean, isF5: boolean, isEY: boolean) => {
  if (isReckitt) {
    return "Welcome to Reckitt's Accelerate Hub!";
  } else if (isSap) {
    return "Welcome to SAP's Acceleration Collective! 👋";
  } else if (isF5) {
    return "Welcome to F5's Volunteer Sprint!";
  } else if (isEY) {
    return 'Welcome to the EY Coaching Program!';
  }
};
